import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Vangnet voor een crash die React niet zelf afvangt.
 *
 * Zonder error boundary haalt React 19 bij zo'n fout de hele boom weg en blijft er een
 * leeg scherm over - in de iOS- en Android-app zonder herlaadknop, dus de gebruiker moest
 * de app wegvegen. Sentry krijgt de fout hoe dan ook binnen; dit is alleen de uitweg.
 *
 * Staat buiten de router en de AuthProvider (zie index.tsx), dus geen Link en geen
 * useAuth: alleen i18n, en dat is al klaar voordat er iets rendert.
 */
export const CrashScreen: React.FC = () => {
  const { t } = useTranslation();

  // Herladen brengt je terug waar je was, en dat is meestal goed: een crash ontstaat vaak
  // door een handeling. Crasht de pagina al bij het laden, dan helpt dat niet; daarvoor is
  // de tweede knop. Coaches gaan naar hun dashboard, publieke bezoekers naar de homepage,
  // net als op de 404-pagina. Een gewone <a> en geen Link: na een crash wil je de app
  // vers laden. Sta je al op het dashboard, dan doet die knop niets extra's en valt weg.
  const pad = window.location.pathname;
  const isCoach = pad.startsWith('/coach');
  const uitweg = isCoach ? '/coach' : '/';
  const toonUitweg = pad !== uitweg;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <h1 className="text-2xl font-semibold text-hockey-brown mb-4">
          {t('crash.title')}
        </h1>
        <p className="text-gray-600 leading-relaxed mb-8">
          {t('crash.body')}
        </p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="inline-block bg-hockey-blue hover:bg-hockey-blue-hover text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            {t('crash.reload')}
          </button>
          {toonUitweg && (
            <a
              href={uitweg}
              className="inline-block border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              {isCoach ? t('crash.toDashboard') : t('crash.toHomepage')}
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
